import React from 'react';
import DateInput from '../../../../muiComponents/DateInput';
import SwitchInput from '../../../../muiComponents/switchInput/SwitchInput';
import TextInput from '../../../../muiComponents/TextInput/TextInput';
import {
    AttributeDefinitionFieldType,
    AttributeDefinitionState,
    AttributeValueState,
} from '../../../../types/properties/propertyTypes';
import Grid from '../../../../muiComponents/Grid';
import GridChild from '../../../../muiComponents/GridChild';
import {
    getDefaultValue,
    getGridChildSize,
} from '../../../../utils/property/propertyZinantisUtils';
import IconButton from '../../../../muiComponents/IconButton';
import Label from '../Label';
import useTranslate from '../../../../customHooks/useTranslate';
import { gridSpacing20 } from '../../../../utils/commonVariables/grid';
import { getRandomNumber } from '../../../../utils/usefulFunctions';

interface InputFieldFactoryProps {
    attributeDefinition: AttributeDefinitionState;
    values: AttributeValueState[];
    onChange: (
        id: string,
        value: string | boolean | Date | number | null,
        idService: string,
        idAttributeDefinition: number
    ) => void;
    onRemove: (
        id: string,
        idService: string,
        idAttributeDefinition: number
    ) => void;
    idService: string;
}

interface FieldControlProps {
    fieldType: AttributeDefinitionFieldType;
    label: string;
    required: boolean;
    disabled: boolean;
    onChange: (value: string | boolean | Date | number | null) => void;
    value?: string | boolean | Date | number | null;
}

const FieldControl = ({
    fieldType,
    label,
    required,
    disabled,
    onChange,
    value = null,
}: FieldControlProps) => {
    switch (fieldType) {
        case AttributeDefinitionFieldType.BOOLEAN:
            return (
                <div className='flex- growflex flex-align-center'>
                    <SwitchInput
                        labelText={label}
                        checked={value === 'True' ? true : false}
                        disabled={disabled}
                        onChangeMethod={(_e: unknown, checked: boolean) => {
                            onChange(checked ? 'True' : 'False');
                        }}
                    />
                </div>
            );
        case AttributeDefinitionFieldType.NUMBER:
            return (
                <TextInput
                    labelText={label}
                    required={required}
                    disabled={disabled}
                    type='number'
                    deleteIcon={false}
                    value={value}
                    handleChange={(_e: unknown, newValue: string) => {
                        onChange(newValue);
                    }}
                />
            );
        case AttributeDefinitionFieldType.DATE:
            return (
                <DateInput
                    labelText={label}
                    disabled={disabled}
                    onChange={(newDate: Date | null) => {
                        onChange(newDate);
                    }}
                    value={value}
                />
            );
        case AttributeDefinitionFieldType.STRING:
        default:
            return (
                <TextInput
                    labelText={label}
                    required={required}
                    disabled={disabled}
                    deleteIcon={false}
                    handleChange={(_e: unknown, newValue: string) => {
                        onChange(newValue);
                    }}
                    value={value}
                />
            );
    }
};

const InputFieldFactory = ({
    attributeDefinition,
    onChange,
    onRemove,
    values,
    idService,
}: InputFieldFactoryProps) => {
    const translate = useTranslate();

    if (attributeDefinition.unique) {
        return (
            <FieldControl
                fieldType={attributeDefinition.fieldType}
                label={attributeDefinition.name}
                required={attributeDefinition.required}
                disabled={false}
                value={values[0]?.value ?? null}
                onChange={value =>
                    onChange(
                        values[0]?.id ?? '',
                        value,
                        idService,
                        attributeDefinition.idAttributeDefinition
                    )
                }
            />
        );
    } else {
        return (
            <div>
                <div className='flex gap-20 flex-align-center'>
                    <Label>
                        {translate(attributeDefinition.name)}
                        {attributeDefinition.required ? (
                            <span className='text-error-color'> *</span>
                        ) : (
                            ''
                        )}
                    </Label>
                    <IconButton
                        style={{ color: 'var(--primary-400)' }}
                        iconName='Add'
                        onClick={() => {
                            onChange(
                                `-${getRandomNumber()}`,
                                getDefaultValue(attributeDefinition.fieldType),
                                idService,
                                attributeDefinition.idAttributeDefinition
                            );
                        }}
                    />
                </div>
                <Grid columnSpacing={gridSpacing20} rowSpacing={gridSpacing20}>
                    {values.map((v, ind) => (
                        <GridChild
                            key={v.id}
                            gridProps={getGridChildSize(
                                attributeDefinition.sizeKey
                            )}
                        >
                            <div
                                className='flex flex-align-center'
                                style={{ width: '100%' }}
                            >
                                <div className='flex-grow'>
                                    <FieldControl
                                        fieldType={
                                            attributeDefinition.fieldType
                                        }
                                        label=''
                                        required={attributeDefinition.required}
                                        disabled={false}
                                        value={v.value}
                                        onChange={value =>
                                            onChange(
                                                v.id,
                                                value,
                                                idService,
                                                attributeDefinition.idAttributeDefinition
                                            )
                                        }
                                    />
                                </div>

                                <IconButton
                                    className='delete-icon-button border-color border-radius-4 padding-4'
                                    iconProps={{
                                        style: { fontSize: '20px' },
                                    }}
                                    style={{
                                        height: '37px',
                                        width: '37px',
                                    }}
                                    iconName='DeleteOutlined'
                                    onClick={() => {
                                        onRemove(
                                            v.id,
                                            idService,
                                            attributeDefinition.idAttributeDefinition
                                        );
                                    }}
                                    disabled={!ind}
                                />
                            </div>
                        </GridChild>
                    ))}
                </Grid>
            </div>
        );
    }
};

export default InputFieldFactory;
